import { useModal } from '@ebay/nice-modal-react';
import { useEffect, useRef } from 'react';

import { SessionStateKeys, useSessionState } from 'hooks/useSessionState';
import useMxCookies from 'modules/Authentication/hooks/useMxCookies';
import ResetKeysModal from 'modules/EndToEndEncryption/ResetKeysModal/ResetKeysModal';

/**
 * After a pre_sync reset is deferred for a **healthy** account (BUG-015 Fix B), `masAlreadyAuthorised`
 * cannot complete the UIA on a standard-UIA server — the session ID was not allocated when the user
 * first went through MAS at click-time (no `session=` param in the URL). `useStartMatrixClient` sets
 * `NEEDS_RESET_KEYS_AFTER_LOGIN` instead of auto-running the deferred reset.
 *
 * This gate reads that flag after login and re-opens `ResetKeysModal` (not in login-unlock mode)
 * so the user can complete a fresh reset with the client started and a properly session-scoped MAS
 * authorisation. Once the modal completes successfully, `ResetKeysModal.handleResetSuccess` sets
 * `NEEDS_SECURITY_CODE_PROMPT_AFTER_BOOTSTRAP` so the security-code creation modal follows.
 *
 * Mounted at the app root alongside `BootstrapSecurityCodeGate` and `EnforceSecurityCodeGate`.
 */
const PostLoginResetGate = () => {
  const [needsReset, , clearNeedsReset] = useSessionState<boolean>(
    SessionStateKeys.NEEDS_RESET_KEYS_AFTER_LOGIN,
    false
  );
  const { mxLoggedIn } = useMxCookies();
  const modal = useModal(ResetKeysModal);
  const hasShownRef = useRef(false);
  const prevNeedsResetRef = useRef<boolean | null>(null);

  // Allow a new show cycle if the flag is set again after being cleared.
  useEffect(() => {
    if (needsReset === true && prevNeedsResetRef.current !== true) {
      hasShownRef.current = false;
    }
    prevNeedsResetRef.current = needsReset;
  }, [needsReset]);

  useEffect(() => {
    if (!needsReset || mxLoggedIn !== true || modal.visible || hasShownRef.current) return;
    hasShownRef.current = true;
    modal.show({ isLoginUnlock: false });
    clearNeedsReset();
  }, [needsReset, mxLoggedIn, modal, clearNeedsReset]);

  return null;
};

export default PostLoginResetGate;
